import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CommentService {
  constructor(
    private http: HttpClient,
    @Inject('ServiceByType') private ServiceByType: any,
  ) {}

  postComment(type: string, id: number, comment: any): Promise<any> {
    return this.ServiceByType.get(type).postComment(id, comment);
  }

  postCommentAndReturnCommentable(type: string, id: number, comment: any): Promise<any> {
    return this.ServiceByType.get(type).postCommentAndReturnCommentable(id, comment);
  }

  deleteComment(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`/api/v1/comments/${id}`))
      .then(body => ({ data: body }));
  }

  editComment(id: number, content: any): Promise<any> {
    return firstValueFrom(this.http.put<any>(`/api/v1/comments/${id}`, content))
      .then(body => ({ data: body }));
  }

  flag(id: number, reason: string): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/comments/${id}/flag`, { reason }))
      .then(body => ({ data: body }));
  }

  like(id: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/comments/${id}/like`, ''))
      .then(body => ({ data: body }));
  }

  unlike(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`/api/v1/comments/${id}/like`))
      .then(body => ({ data: body }));
  }

  hide(id: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/comments/${id}/hide`, ''))
      .then(body => ({ data: body }));
  }

  publish(id: number): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/comments/${id}/publish`, ''))
      .then(body => ({ data: body }));
  }

  toggleHide(item: { id: number; state: string }): Promise<any> {
    return item.state === 'hidden' ? this.publish(item.id) : this.hide(item.id);
  }

  addIndentation(comment: any, indentations: Record<number, number>): any {
    if (indentations[comment.id] === undefined) {
      indentations[comment.id] = 0;
    }
    if (comment.parent_id) {
      if (indentations[comment.parent_id] === undefined) {
        indentations[comment.parent_id] = 0;
      }
      indentations[comment.id] = indentations[comment.parent_id] + 1;
    }
    comment.indentation = indentations[comment.id];
    return comment;
  }

  getCommentableLabel(item: any): string | undefined {
    if (item.type === 'status') return `a ${item.type}`;
    if (item.title !== undefined) return item.title;
    if (item.name !== undefined) return item.name;
  }

  likedUsers(id: number, parameters?: any): Promise<any> {
    const params = { page: 1, per_page: 10, ...parameters };
    return firstValueFrom(
      this.http.get<any>(`/api/v1/comments/${id}/liked_users`, { observe: 'response', params }),
    ).then(response => ({
      data: response.body,
      headers: (name: string) => response.headers.get(name),
    }));
  }

  feed(params: any): Promise<any> {
    let type: string;
    switch (params.type) {
      case 'status': type = 'statuses'; break;
      case 'topic':
      case 'article':
      case 'course': type = `${params.type}s`; break;
      default: return Promise.reject(new Error(`Unsupported comment feed type: ${params.type}`));
    }
    const filteredParams = { after_id: params.after_id, per_page: params.per_page };
    return firstValueFrom(
      this.http.get<any>(`/api/v1/${type}/${params.id}/comments/flat`, { observe: 'response', params: filteredParams }),
    ).then(response => ({
      data: response.body,
      headers: (name: string) => response.headers.get(name),
    }));
  }
}
